import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, TrendingUp, TrendingDown, DollarSign, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinanceManager = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [formData, setFormData] = useState({
    type: 'income',
    amount: 0,
    description: '',
    category: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/finance/records`, { withCredentials: true, params: { mosque_id: user?.mosque_id } });
      setRecords(data);
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRecord) {
        await axios.put(
          `${API_URL}/api/finance/records/${editingRecord.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Transaksi berhasil diupdate!');
      } else {
        await axios.post(
          `${API_URL}/api/finance/records`,
          formData,
          { withCredentials: true }
        );
        toast.success('Transaksi berhasil ditambahkan!');
      }
      setShowModal(false);
      setEditingRecord(null);
      resetForm();
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan transaksi');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus transaksi ini?')) return;
    try {
      await axios.delete(`${API_URL}/api/finance/records/${id}`, { withCredentials: true });
      toast.success('Transaksi berhasil dihapus!');
      fetchRecords();
    } catch (error) {
      toast.error('Gagal menghapus transaksi');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      type: record.type,
      amount: record.amount,
      description: record.description,
      category: record.category,
      date: record.date
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      type: 'income',
      amount: 0,
      description: '',
      category: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Filter records
  const filteredRecords = records.filter(record => {
    if (filterType !== 'all' && record.type !== filterType) return false;
    if (filterMonth && !record.date.startsWith(filterMonth)) return false;
    return true;
  });

  // Calculate totals
  const totalIncome = filteredRecords
    .filter(r => r.type === 'income')
    .reduce((sum, r) => sum + r.amount, 0);
  const totalExpense = filteredRecords
    .filter(r => r.type === 'expense')
    .reduce((sum, r) => sum + r.amount, 0);
  const balance = totalIncome - totalExpense;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-200 border-t-emerald-700" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-primary-dark">Manajemen Keuangan</h2>
          <p className="text-sm font-body text-text-secondary mt-1">
            Kelola pemasukan dan pengeluaran masjid
          </p>
        </div>
        <button
          data-testid="add-finance-btn"
          onClick={() => {
            setEditingRecord(null);
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          Tambah Transaksi
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-body text-text-secondary">Total Pemasukan</p>
              <p className="text-2xl font-body font-bold text-green-700">
                {formatRupiah(totalIncome)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-body text-text-secondary">Total Pengeluaran</p>
              <p className="text-2xl font-body font-bold text-red-700">
                {formatRupiah(totalExpense)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-700" />
            </div>
            <div>
              <p className="text-sm font-body text-text-secondary">Saldo</p>
              <p className={`text-2xl font-body font-bold ${balance >= 0 ? 'text-emerald-800' : 'text-red-700'}`}>
                {formatRupiah(balance)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-stone-200 p-4 mb-6 flex gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-stone-500" />
          <span className="text-sm font-body font-medium text-text-primary">Filter:</span>
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 rounded-lg border border-stone-200 text-sm font-body focus:outline-none focus:ring-2 focus:ring-emerald-200"
        >
          <option value="all">Semua Transaksi</option>
          <option value="income">Pemasukan</option>
          <option value="expense">Pengeluaran</option>
        </select>
        <input
          type="month"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          className="px-3 py-2 rounded-lg border border-stone-200 text-sm font-body focus:outline-none focus:ring-2 focus:ring-emerald-200"
        />
      </div>

      {/* Records Table */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        {filteredRecords.length === 0 ? (
          <div className="p-12 text-center">
            <DollarSign className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-text-secondary font-body mb-4">Belum ada transaksi</p>
            <button
              onClick={() => setShowModal(true)}
              className="text-emerald-700 font-body font-medium hover:underline"
            >
              Tambah transaksi pertama
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-body font-semibold text-text-secondary uppercase">
                    Tanggal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-body font-semibold text-text-secondary uppercase">
                    Tipe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-body font-semibold text-text-secondary uppercase">
                    Kategori
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-body font-semibold text-text-secondary uppercase">
                    Deskripsi
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-body font-semibold text-text-secondary uppercase">
                    Jumlah
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-body font-semibold text-text-secondary uppercase">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {filteredRecords.map((record) => (
                  <tr key={record.id} className="hover:bg-stone-50" data-testid={`finance-item-${record.id}`}>
                    <td className="px-6 py-4 text-sm font-body text-text-primary">
                      {formatDate(record.date)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                          record.type === 'income'
                            ? 'bg-green-50 text-green-700'
                            : 'bg-red-50 text-red-700'
                        }`}
                      >
                        {record.type === 'income' ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        {record.type === 'income' ? 'Pemasukan' : 'Pengeluaran'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-body text-text-primary">
                      {record.category}
                    </td>
                    <td className="px-6 py-4 text-sm font-body text-text-secondary">
                      {record.description}
                    </td>
                    <td className="px-6 py-4 text-sm font-body font-bold text-right">
                      <span className={record.type === 'income' ? 'text-green-700' : 'text-red-700'}>
                        {record.type === 'expense' && '-'}
                        {formatRupiah(record.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          data-testid={`edit-finance-${record.id}`}
                          onClick={() => handleEdit(record)}
                          className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4 text-stone-600" />
                        </button>
                        <button
                          data-testid={`delete-finance-${record.id}`}
                          onClick={() => handleDelete(record.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Form */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-heading font-bold text-primary-dark">
                  {editingRecord ? 'Edit Transaksi' : 'Tambah Transaksi Baru'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingRecord(null);
                    resetForm();
                  }}
                  className="p-2 hover:bg-stone-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Tipe Transaksi *
                  </label>
                  <select
                    data-testid="finance-type-input"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    required
                  >
                    <option value="income">Pemasukan</option>
                    <option value="expense">Pengeluaran</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-body font-medium text-text-primary mb-2">
                      Jumlah (Rp) *
                    </label>
                    <input
                      type="number"
                      data-testid="finance-amount-input"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                      placeholder="1000000"
                      min="0"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-body font-medium text-text-primary mb-2">
                      Tanggal *
                    </label>
                    <input
                      type="date"
                      data-testid="finance-date-input"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Kategori *
                  </label>
                  <input
                    type="text"
                    data-testid="finance-category-input"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    placeholder="Contoh: Donasi, Listrik, Gaji, dll"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-text-primary mb-2">
                    Deskripsi *
                  </label>
                  <textarea
                    data-testid="finance-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none font-body"
                    rows={3}
                    placeholder="Detail transaksi..."
                    required
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingRecord(null);
                      resetForm();
                    }}
                    className="flex-1 px-6 py-3 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-full font-body font-medium transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    data-testid="finance-submit-btn"
                    className="flex-1 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full font-body font-medium transition-colors"
                  >
                    {editingRecord ? 'Update' : 'Simpan'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FinanceManager;
